import React, { useState, useRef, useEffect } from 'react';
import QRCode from 'qrcode';
import { API_BASE_URL } from '../config';
import '../styles/AdminGuestRegistration.css';

const AdminGuestRegistration = () => {
  const [step, setStep] = useState('camera'); // camera, details, badge
  const [photo, setPhoto] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userId, setUserId] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [registeredGuests, setRegisteredGuests] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Initialize camera
  useEffect(() => {
    if (step === 'camera') {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [step]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (video && canvas) {
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);

      // Convert to base64
      const photoData = canvas.toDataURL('image/jpeg', 0.8);
      setPhoto(photoData);
      setStep('details');
      stopCamera();
    }
  };

  const retakePhoto = () => {
    setPhoto(null);
    setStep('camera');
    setUsername('');
    setPassword('');
    setError('');
  };

  const registerGuest = async () => {
    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    if (password.length < 4) {
      setError('Password must be at least 4 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Step 1: Register via existing endpoint (creates user with QR)
      const registerResponse = await fetch(`${API_BASE_URL}/api/admin/register-guest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': localStorage.getItem('adminKey') || ''
        },
        body: JSON.stringify({
          username,
          password,
          profilePicture: photo
        })
      });

      if (!registerResponse.ok) {
        throw new Error('Registration failed');
      }

      const data = await registerResponse.json();

      // Step 2: Generate QR code with login credentials
      const loginData = {
        username,
        password,
        userId: data.userId
      };

      const qrUrl = await QRCode.toDataURL(JSON.stringify(loginData), {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });

      setUserId(data.userId);
      setQrCodeUrl(qrUrl);
      setStep('badge');

      // Add to registered guests list
      setRegisteredGuests(prev => [{
        userId: data.userId,
        username,
        photo,
        qrCodeUrl: qrUrl,
        timestamp: new Date()
      }, ...prev]);

    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Failed to register guest. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const printBadge = () => {
    window.print();
  };

  const registerAnother = () => {
    setStep('camera');
    setPhoto(null);
    setUsername('');
    setPassword('');
    setUserId(null);
    setQrCodeUrl('');
    setError('');
  };

  const downloadBadge = () => {
    // Create a temporary canvas to combine photo and QR
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 800;
    const ctx = canvas.getContext('2d');

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Title
    ctx.fillStyle = '#6a11cb';
    ctx.font = 'bold 32px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('STOCK MARKET PARTY', canvas.width / 2, 50);

    // Load and draw photo
    const photoImg = new Image();
    photoImg.onload = () => {
      ctx.drawImage(photoImg, 50, 100, 200, 200);

      // Load and draw QR code
      const qrImg = new Image();
      qrImg.onload = () => {
        ctx.drawImage(qrImg, 350, 100, 200, 200);

        // Username
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 28px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(username, canvas.width / 2, 350);

        // Trader ID
        ctx.font = '20px Arial';
        ctx.fillStyle = '#666666';
        ctx.fillText(`Trader ID: ${userId}`, canvas.width / 2, 390);

        // Instructions
        ctx.font = '18px Arial';
        ctx.fillText('Scan QR Code to Login', canvas.width / 2, 450);

        // Starting balance
        ctx.font = 'bold 24px Arial';
        ctx.fillStyle = '#00ff88';
        ctx.fillText('Starting Balance: $10,000', canvas.width / 2, 500);

        // Download
        canvas.toBlob(blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `badge-${username}.png`;
          a.click();
          URL.revokeObjectURL(url);
        });
      };
      qrImg.src = qrCodeUrl;
    };
    photoImg.src = photo;
  };

  return (
    <div className="admin-guest-registration">
      <div className="registration-header">
        <h1>🎉 Guest Registration Station</h1>
        <p>Take photo, collect username/password, generate badge</p>
      </div>

      {error && (
        <div className="error-banner">
          ⚠️ {error}
        </div>
      )}

      {/* Camera Step */}
      {step === 'camera' && (
        <div className="camera-section">
          <div className="camera-container">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="camera-feed"
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>

          <div className="camera-controls">
            <button className="btn-large btn-primary" onClick={capturePhoto}>
              📸 Capture Photo
            </button>
            <p className="hint">Position guest in frame and take their photo</p>
          </div>
        </div>
      )}

      {/* Details Step */}
      {step === 'details' && (
        <div className="details-section">
          <div className="photo-preview">
            <img src={photo} alt="Guest" />
            <button className="btn-small" onClick={retakePhoto}>
              🔄 Retake Photo
            </button>
          </div>

          <div className="details-form">
            <div className="form-group">
              <label>Username (ask guest):</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username..."
                className="input-large"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && document.getElementById('password-input').focus()}
              />
            </div>

            <div className="form-group">
              <label>Password (guest whispers or types):</label>
              <div className="password-input-wrapper">
                <input
                  id="password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password..."
                  className="input-large"
                  onKeyPress={(e) => e.key === 'Enter' && registerGuest()}
                />
                <button
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              <p className="hint">Min 4 characters • They'll use this to login</p>
            </div>

            <div className="form-actions">
              <button
                className="btn-large btn-primary"
                onClick={registerGuest}
                disabled={loading || !username || !password}
              >
                {loading ? '⏳ Creating Account...' : '✅ Register & Generate Badge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Badge Step */}
      {step === 'badge' && (
        <div className="badge-section">
          <div className="badge-preview" id="printable-badge">
            <div className="badge-card">
              <div className="badge-header">
                <h2>STOCK MARKET PARTY 2024</h2>
                <div className="badge-subtitle">Trader Badge</div>
              </div>

              <div className="badge-content">
                <div className="badge-photo">
                  <img src={photo} alt={username} />
                </div>

                <div className="badge-qr">
                  <img src={qrCodeUrl} alt="QR Code" />
                  <p className="qr-label">Scan to Login</p>
                </div>
              </div>

              <div className="badge-info">
                <div className="badge-username">{username}</div>
                <div className="badge-id">Trader ID: {userId}</div>
                <div className="badge-balance">Starting Balance: $10,000</div>
              </div>

              <div className="badge-instructions">
                <p>📱 Scan QR code with your phone</p>
                <p>🚀 Start trading immediately!</p>
              </div>
            </div>
          </div>

          <div className="badge-actions">
            <button className="btn-large btn-primary" onClick={printBadge}>
              🖨️ Print Badge
            </button>
            <button className="btn-large btn-secondary" onClick={downloadBadge}>
              💾 Download Badge
            </button>
            <button className="btn-large btn-success" onClick={registerAnother}>
              ➕ Register Another Guest
            </button>
          </div>

          <div className="success-message">
            ✅ {username} registered successfully! Show them the QR code or print the badge.
          </div>
        </div>
      )}

      {/* Recently Registered Guests */}
      {registeredGuests.length > 0 && (
        <div className="registered-guests">
          <h3>📋 Recently Registered ({registeredGuests.length})</h3>
          <div className="guests-grid">
            {registeredGuests.slice(0, 6).map((guest, index) => (
              <div key={index} className="guest-card">
                <img src={guest.photo} alt={guest.username} className="guest-thumbnail" />
                <div className="guest-name">{guest.username}</div>
                <div className="guest-time">
                  {guest.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGuestRegistration;
